import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { fieldMappingsService } from '@/services/control/field-mappings-service';
import type { FieldMappingSeedResult } from '@/types/control/field-mapping';

import { adminMutationKeys, adminQueryKeys } from '@/hooks/admin/query-keys';

/**
 * Seed a tenant's baseline mappings.
 *
 * Seeding is idempotent per mapping: the result reports `created` against
 * `totalDefaults`, and the difference is what already existed and was skipped.
 * So running it twice is safe and the second run reports `created: 0`.
 */
export function useSeedFieldMappings(
  tenantId: string
): UseMutationResult<FieldMappingSeedResult, Error, void> {
  const queryClient = useQueryClient();

  return useMutation<FieldMappingSeedResult, Error, void>({
    mutationKey: adminMutationKeys.seedFieldMappings(tenantId),
    mutationFn: () => fieldMappingsService.seedDefaults(tenantId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.fieldMappingsByTenant(tenantId),
      }),
  });
}
